import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface OwnerInputProps {
  owners: string[];
  availableOwners: string[];
  onOwnersChange: (owners: string[]) => void;
  className?: string;
  placeholder?: string;
  maxOwners?: number;
}

const OwnerInput: React.FC<OwnerInputProps> = ({
  owners,
  availableOwners,
  onOwnersChange,
  className = '',
  placeholder = 'Add owner...',
  maxOwners = 10
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredOwners, setFilteredOwners] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter available owners based on input value and exclude already selected owners
  useEffect(() => {
    if (inputValue) {
      const filtered = availableOwners.filter(owner =>
        owner.toLowerCase().includes(inputValue.toLowerCase()) &&
        !owners.includes(owner)
      );
      setFilteredOwners(filtered);
    } else {
      // When input is empty, show all available owners that aren't already selected
      const filtered = availableOwners.filter(owner => !owners.includes(owner));
      setFilteredOwners(filtered);
    }
  }, [inputValue, availableOwners, owners]);

  const addOwner = (owner: string) => {
    const trimmedOwner = owner.trim();
    if (trimmedOwner && !owners.includes(trimmedOwner) && owners.length < maxOwners) {
      onOwnersChange([...owners, trimmedOwner]);
    }
    setInputValue('');
    setIsDropdownOpen(false);
  };

  const removeOwner = (ownerToRemove: string) => {
    onOwnersChange(owners.filter(owner => owner !== ownerToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addOwner(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && owners.length > 0) {
      // Remove last owner when backspace is pressed on empty input
      removeOwner(owners[owners.length - 1]);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setInputValue('');
    } else if (e.key === 'ArrowDown' && filteredOwners.length > 0) {
      e.preventDefault();
      // Focus first suggestion
      const firstSuggestion = document.querySelector('.owner-suggestion') as HTMLButtonElement;
      firstSuggestion?.focus();
    }
  };

  const handleSuggestionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, owner: string, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOwner(owner);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        inputRef.current?.focus();
      } else {
        const prevSuggestion = document.querySelectorAll('.owner-suggestion')[index - 1] as HTMLButtonElement;
        prevSuggestion?.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextSuggestion = document.querySelectorAll('.owner-suggestion')[index + 1] as HTMLButtonElement;
      if (nextSuggestion) {
        nextSuggestion.focus();
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="w-full min-h-[42px] border border-edge-input rounded-md px-3 py-2 focus-within:ring-primary-500 focus-within:border-primary-500">
        <div className="flex flex-wrap gap-2 mb-2">
          {owners.map((owner, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800"
            >
              {owner}
              <button
                data-track="owner_remove"
                type="button"
                onClick={() => removeOwner(owner)}
                className="ml-1 text-green-700 hover:text-green-900 focus:outline-none"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsDropdownOpen(true);
          }}
          onBlur={() => {
            // Delay hiding dropdown to allow clicking on suggestions
            setTimeout(() => setIsDropdownOpen(false), 200);
          }}
          placeholder={owners.length >= maxOwners ? `Maximum ${maxOwners} owners` : placeholder}
          disabled={owners.length >= maxOwners}
          className="w-full border-none outline-none flex-1 min-w-0 bg-transparent"
        />
      </div>

      {/* Dropdown for suggestions */}
      {isDropdownOpen && (
        <div className="absolute z-10 w-full mt-1 bg-surface border border-edge-input rounded-md shadow-lg">
          {filteredOwners.length > 0 ? (
            <ul className="max-h-40 overflow-y-auto">
              {filteredOwners.map((owner, index) => (
                <li key={owner}>
                  <button
                    data-track="owner_suggestion"
                    type="button"
                    className="owner-suggestion w-full px-3 py-2 text-left hover:bg-green-50 focus:bg-green-50 focus:outline-none"
                    onClick={() => addOwner(owner)}
                    onKeyDown={(e) => handleSuggestionKeyDown(e, owner, index)}
                  >
                    {owner}
                  </button>
                </li>
              ))}
            </ul>
          ) : inputValue ? (
            <div className="px-3 py-2">
              <button
                data-track="owner_add"
                type="button"
                className="owner-suggestion w-full text-left text-green-700 hover:bg-green-50 focus:bg-green-50 focus:outline-none py-1"
                onClick={() => addOwner(inputValue)}
                onKeyDown={(e) => handleSuggestionKeyDown(e, inputValue, 0)}
              >
                Create "{inputValue}"
              </button>
            </div>
          ) : filteredOwners.length === 0 && availableOwners.length > 0 ? (
            <div className="px-3 py-2 text-muted text-sm">
              All available owners have been added
            </div>
          ) : null}
        </div>
      )}

      {owners.length >= maxOwners && (
        <p className="text-xs text-muted mt-1">
          Maximum of {maxOwners} owners reached
        </p>
      )}
    </div>
  );
};

export default OwnerInput;